import { captureDatadogError } from "@/lib/analytics-loader";
import { sessionQueryKey } from "@/lib/queries/session";
import { queryClient } from "@/lib/query";
import { Button } from "@repo/ui";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

interface AuthErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

// Error type that may include status code
// NOTE: This extends Error to handle both tRPC errors (with status) and native JS errors
interface ErrorWithStatus extends Error {
  status?: number;
}

const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload";

// Detect stale chunk errors that occur after a new deployment when a user's
// browser tries to lazy-load a JS chunk that no longer exists.
// Cloudflare's SPA fallback returns index.html (text/html) instead of JS.
function isStaleChunkError(error: unknown): boolean {
  const message = ((error as Error)?.message || "").toLowerCase();
  return (
    message.includes("is not a valid javascript mime type") ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("error loading dynamically imported module")
  );
}

// Detect transient network errors (iOS Safari "Load failed", Chrome "Failed to fetch")
// that occur when fetch() is interrupted by connectivity loss or background tab killing.
function isTransientNetworkError(error: unknown): boolean {
  const message = ((error as Error)?.message || "").toLowerCase();
  return message === "load failed" || message === "failed to fetch";
}

// Detect DOM reconciliation errors caused by external code (browser extensions,
// PostHog site apps) mutating DOM nodes that React manages.
function isDomReconciliationError(error: unknown): boolean {
  const message = ((error as Error)?.message || "").toLowerCase();
  return (
    (message.includes("insertbefore") || message.includes("removechild")) &&
    message.includes("on 'node'")
  );
}

// Determine if an error is authentication-related
// WARNING: This function must catch all auth errors to prevent infinite error loops
// when wrapped components try to access protected resources
function isAuthError(error: unknown): boolean {
  // Check for explicit status codes
  const status = (error as ErrorWithStatus)?.status;
  if (status === 401 || status === 403) return true;

  // Check for auth-related error messages
  const message = (error as Error)?.message?.toLowerCase() || "";
  return (
    message.includes("unauthorized") ||
    message.includes("unauthenticated") ||
    message.includes("session expired") ||
    message.includes("401") ||
    message.includes("403")
  );
}

// Fallback component for auth errors
function AuthErrorFallback({
  error,
  resetErrorBoundary,
}: AuthErrorFallbackProps) {
  const handleRetry = () => {
    // Reset React Query errors and component errors
    // NOTE: resetQueries() clears error state but preserves cached data,
    // allowing immediate retry without full data refetch
    queryClient.resetQueries();
    resetErrorBoundary();
  };

  const handleSignIn = () => {
    // Clear auth caches and redirect to login
    // WARNING: removeQueries() permanently deletes cached auth data.
    // Using window.location.href (not router navigation) ensures full page reload,
    // clearing all React state that might hold stale auth tokens
    queryClient.removeQueries({ queryKey: ["auth"] });
    window.location.href = "/login";
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center p-6">
      <div className="mx-auto max-w-md text-center">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
        <h1 className="mb-2 text-2xl font-bold">Authentication Required</h1>
        <p className="mb-6 text-muted-foreground">
          {(error as Error)?.message === "Unauthorized" ||
          (error as ErrorWithStatus)?.status === 401
            ? "Your session has expired. Please sign in again."
            : "You need to sign in to access this page."}
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={handleRetry}>
            Try Again
          </Button>
          <Button onClick={handleSignIn}>Sign In</Button>
        </div>
      </div>
    </div>
  );
}

interface AuthErrorBoundaryProps {
  children: ReactNode;
}

// General error fallback that handles both auth and other errors
// This wrapper ensures auth errors get special UI treatment while preserving
// generic error handling for all other failures
function ErrorFallbackWrapper({
  error,
  resetErrorBoundary,
}: AuthErrorFallbackProps) {
  // Stale chunk errors auto-reload in onError, but if the reload already happened
  // and we still hit this fallback, show a manual reload prompt
  if (isStaleChunkError(error)) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-6">
        <div className="mx-auto max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h1 className="mb-2 text-2xl font-bold">Update Available</h1>
          <p className="mb-6 text-muted-foreground">
            A new version of the app is available. Please reload to continue.
          </p>
          <Button onClick={() => window.location.reload()}>Reload Page</Button>
        </div>
      </div>
    );
  }

  // Transient network errors — show a "Connection Issue" prompt instead of the
  // raw "Load failed" / "Failed to fetch" message that confuses users.
  if (isTransientNetworkError(error)) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-6">
        <div className="mx-auto max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h1 className="mb-2 text-2xl font-bold">Connection Issue</h1>
          <p className="mb-6 text-muted-foreground">
            A network request failed. Please check your connection and try
            again.
          </p>
          <Button onClick={resetErrorBoundary}>Try Again</Button>
        </div>
      </div>
    );
  }

  // If it's not an auth error, show a generic error message
  if (!isAuthError(error)) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center p-6">
        <div className="mx-auto max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h1 className="mb-2 text-2xl font-bold">Something went wrong</h1>
          <p className="mb-6 text-muted-foreground">
            {(error as Error)?.message || "An unexpected error occurred"}
          </p>
          <Button onClick={resetErrorBoundary}>Try Again</Button>
        </div>
      </div>
    );
  }

  // For auth errors, use the auth-specific UI
  return (
    <AuthErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
  );
}

// Modern auth error boundary using react-error-boundary
export function AuthErrorBoundary({ children }: AuthErrorBoundaryProps) {
  const { reset } = useQueryErrorResetBoundary();
  const domRetryRef = useRef(0);
  const [boundaryKey, setBoundaryKey] = useState(0);

  return (
    <ErrorBoundary
      key={boundaryKey}
      FallbackComponent={ErrorFallbackWrapper}
      onReset={() => {
        domRetryRef.current = 0;
        reset();
      }}
      onError={(error, errorInfo) => {
        // Auto-retry DOM reconciliation errors once by remounting the boundary.
        // These are caused by third-party scripts (PostHog, browser extensions)
        // mutating DOM nodes that React manages. A fresh render rebuilds the
        // DOM tree from scratch, resolving the transient conflict.
        if (isDomReconciliationError(error)) {
          console.warn(
            "DOM reconciliation error (likely browser extension):",
            error,
          );
          if (domRetryRef.current < 1) {
            domRetryRef.current++;
            setTimeout(() => setBoundaryKey((k) => k + 1), 0);
            return;
          }
          // Exhausted retries — show generic error UI
          console.warn("DOM reconciliation error persists after retry:", error);
          return;
        }

        // Auto-reload ONCE for stale chunk errors (post-deployment lazy import failures).
        // Uses sessionStorage flag to ensure only one auto-reload per session,
        // preventing reload loops when returning to the tab.
        if (isStaleChunkError(error)) {
          console.warn("Stale chunk error detected:", error);
          try {
            const alreadyReloaded = sessionStorage.getItem(
              STALE_CHUNK_RELOAD_KEY,
            );
            if (!alreadyReloaded) {
              sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, "1");
              window.location.reload();
              return;
            }
          } catch {
            // Storage unavailable — skip auto-reload to avoid infinite loops.
            // Falls through to the "Update Available" fallback UI instead.
          }
          return;
        }

        // Transient network errors (iOS Safari "Load failed") — log as warn
        // and let the fallback UI offer a retry. Not actionable in Datadog.
        if (isTransientNetworkError(error)) {
          console.warn("Transient network error caught by boundary:", error);
          return;
        }

        // Clear stale session data for auth errors
        // NOTE: sessionQueryKey is imported from queries/session - ensures we clear
        // the exact query key used by useSession() hook to prevent stale auth state
        if (isAuthError(error)) {
          console.warn("Auth error caught by boundary:", error);
          queryClient.removeQueries({ queryKey: sessionQueryKey });
          return;
        }

        // Only log truly unexpected errors to console.error (captured by Datadog RUM).
        // Handled cases above use console.warn to avoid noisy error tracking.
        console.error("Unexpected error caught by boundary:", error, errorInfo);

        // EPD-6938: forward the error to Datadog explicitly with React's
        // componentStack. React's error-boundary path is the only place
        // where we get a fully-resolved component stack (component names
        // like `<NavOrgSwitcher>`, `<Marketplace>`, etc.) even when the
        // raw JS stack is frame-less (iOS Safari swallows stacks). Without
        // this hook, console.error gives us the message but not the
        // owning component — which is the missing signal we've needed
        // across multiple investigations of the recurring `o.id` crash.
        const errorInstance =
          error instanceof Error ? error : new Error(String(error));
        void captureDatadogError(errorInstance, {
          source: "auth-error-boundary",
          componentStack: errorInfo.componentStack ?? null,
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
